import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Beaker, BarChart3, Mail, FlaskConical, ScanLine, ShieldCheck } from 'lucide-react';
import { AppLayout } from '../components/Layout';
import AdminOrganizationsPage from './AdminOrganizationsPage';
import LabsAdminPage from './LabsAdminPage';
import AiEconomicsPage from './AiEconomicsPage';
import NotificationsPanel from '../components/admin/NotificationsPanel';
import ExperienceAuditPanel from '../components/admin/ExperienceAuditPanel';

const TABS = [
  { key: 'demos', label: 'Demos', icon: Beaker,
    note: 'Sales and investor walkthroughs, throwaway tenants and lab sessions' },
  { key: 'pilots', label: 'Pilots', icon: FlaskConical,
    note: 'Organizations trying it with real young people before they pay' },
  { key: 'clients', label: 'Paid Clients', icon: Building2, note: 'Organizations paying for seats' },
  { key: 'audit', label: 'Experience Audit', icon: ScanLine,
    note: 'Every screen judged against every archetype we serve' },
  { key: 'economics', label: 'AI Economics', icon: BarChart3, note: 'Cost, routing mix, margin' },
  { key: 'notifications', label: 'Notifications', icon: Mail, note: 'Send the real emails and check them' },
];

/** One administrative roof. Tenants and Labs were two places doing related work; now they aren't. */
export default function AdminConsolePage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const legacy = { tenants: 'clients', labs: 'demos' };
  const asked = params.get('tab');
  const tab = TABS.some(t => t.key === asked) ? asked : (legacy[asked] || 'clients');

  return (
    <AppLayout>
      <div className="px-6 md:px-8 pt-6" data-testid="admin-console-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">
            Agentic FuturePath AI · admin console
          </p>
          <button onClick={() => navigate('/app/admin/trust-compliance')}
            data-testid="open-trust-compliance"
            className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40
              bg-cyan-400/[0.10] px-4 py-1.5 text-[12px] font-semibold text-cyan-100
              hover:bg-cyan-400/[0.20] transition-colors">
            <ShieldCheck className="w-3.5 h-3.5" /> Trust &amp; Compliance
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-4 border-b border-white/10 pb-px">
          {TABS.map(t => {
            const on = t.key === tab;
            return (
              <button key={t.key} onClick={() => setParams({ tab: t.key })}
                data-testid={`admin-tab-${t.key}`} title={t.note}
                className={`relative inline-flex items-center gap-2 rounded-t-xl px-4 py-2.5 text-sm transition-colors ${on
                  ? 'text-white bg-white/[0.06]' : 'text-slate-300 hover:text-slate-200'}`}>
                <t.icon className="w-4 h-4" />
                {t.label}
                {on && (
                  <motion.span layoutId="admin-tab-underline"
                    className="absolute left-3 right-3 -bottom-px h-0.5 rounded-full bg-cyan-400" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="pb-4">
        {tab === 'clients' && <AdminOrganizationsPage embedded licence="paid" />}
        {tab === 'pilots' && <AdminOrganizationsPage embedded licence="pilot" />}
        {tab === 'demos' && (
          <>
            <AdminOrganizationsPage embedded licence="demo" />
            <div className="px-6 md:px-8">
              <div className="border-t border-white/10 pt-2" />
            </div>
            <LabsAdminPage embedded />
          </>
        )}
        {tab === 'economics' && <AiEconomicsPage />}
        {tab === 'audit' && (
          <div className="p-6 md:p-8 max-w-6xl mx-auto"><ExperienceAuditPanel /></div>
        )}
        {tab === 'notifications' && (
          <div className="p-6 md:p-8 max-w-5xl mx-auto"><NotificationsPanel /></div>
        )}
      </div>
    </AppLayout>
  );
}
